import React, { useCallback } from 'react';
import { RiDragMove2Line, RiHand, RiLayoutGridLine } from 'react-icons/ri';

import { CHANGE_POINTER_ICON } from '../../actions/changePointerIcon';
import { SET_EDITOR_PREFERENCES } from '../../actions/setEditorPreferences';
import { useStore } from '../../hooks';
import { CANVAS_INTERACTION_MODES, POINTER_ICONS } from '../../utils/constants';
import {
  canUsePanMode,
  getCanvasInteractionMode,
} from '../../utils/canvasInteractionMode';

const CanvasModeToolbar = () => {
  const {
    dispatch,
    editorPreferences = {},
    zoom = {},
    canvasWidth,
    canvasHeight,
    originalImage,
    shownImageDimensions,
    resize,
    t,
  } = useStore();
  const panAvailable = canUsePanMode({
    zoom,
    canvasWidth,
    canvasHeight,
    originalImage,
    shownImageDimensions,
    resize,
  });
  const panUnavailable = !panAvailable;
  const interactionMode = getCanvasInteractionMode(editorPreferences);
  const isSelectMode = interactionMode === CANVAS_INTERACTION_MODES.SELECT;
  const isPanMode = interactionMode === CANVAS_INTERACTION_MODES.PAN;
  const snapOn = Boolean(editorPreferences.snapToGuides);

  const setInteractionMode = useCallback(
    (mode) => {
      dispatch({
        type: SET_EDITOR_PREFERENCES,
        payload: { canvasInteractionMode: mode },
      });
      dispatch({
        type: CHANGE_POINTER_ICON,
        payload: {
          pointerCssIcon:
            mode === CANVAS_INTERACTION_MODES.PAN
              ? POINTER_ICONS.DRAG
              : POINTER_ICONS.DEFAULT,
        },
      });
    },
    [dispatch],
  );

  const setSnap = useCallback(
    (enabled) => {
      dispatch({
        type: SET_EDITOR_PREFERENCES,
        payload: { snapToGuides: enabled },
      });
    },
    [dispatch],
  );

  return (
    <div
      className="respark_canvas-mode-toolbar"
      role="toolbar"
      aria-label={t('canvasModeToolbarTitle')}
    >
      <button
        type="button"
        className={`respark_canvas-mode-toolbar__btn${
          isSelectMode ? ' respark_canvas-mode-toolbar__btn--active' : ''
        }`}
        title={t('canvasModeSelectTitle')}
        aria-label={t('canvasModeSelect')}
        aria-pressed={isSelectMode}
        onClick={() => setInteractionMode(CANVAS_INTERACTION_MODES.SELECT)}
      >
        <RiDragMove2Line size={16} aria-hidden />
        <span>{t('canvasModeSelect')}</span>
      </button>
      <button
        type="button"
        className={`respark_canvas-mode-toolbar__btn${
          isPanMode ? ' respark_canvas-mode-toolbar__btn--active' : ''
        }`}
        title={
          panUnavailable && !isPanMode
            ? t('canvasModePanTitleZoomIn')
            : t('canvasModePanTitle')
        }
        aria-label={t('canvasModePan')}
        aria-pressed={isPanMode}
        disabled={panUnavailable && !isPanMode}
        onClick={() => setInteractionMode(CANVAS_INTERACTION_MODES.PAN)}
      >
        <RiHand size={16} aria-hidden />
        <span>{t('canvasModePan')}</span>
      </button>
      <button
        type="button"
        className={`respark_canvas-mode-toolbar__btn${
          snapOn ? ' respark_canvas-mode-toolbar__btn--active' : ''
        }`}
        title={t('snapToGuidesTitle')}
        aria-label={t('smartGuidesShort')}
        aria-pressed={snapOn}
        onClick={() => setSnap(!snapOn)}
      >
        <RiLayoutGridLine size={16} aria-hidden />
        <span>{t('smartGuidesShort')}</span>
      </button>
    </div>
  );
};

export default CanvasModeToolbar;
